import {Action, Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';

import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  Section,
  UploadDocumentsDialog,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {TestIdProps, suffixTestId} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

interface DocumentsProps extends TestIdProps {
  onDocumentAction: VoidFunction;
}

export function Documents(props: DocumentsProps) {
  const {fastTrackIssueId} = useWarehouseParams();

  const [canEditFastTrackIssue] = usePermissions({
    permissionKeys: ['fastTrackIssueEdit'],
  });

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const [rowActions] = useGetDocumentDatagridActions({
    contextId: fastTrackIssueId,
    contextTarget: CONTEXT_TARGET,
    refreshData: () => refreshDmsDatagrid(DOCUMENTS_GRID_CODE),
  });

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {contextTargetId: fastTrackIssueId}]),
    [fastTrackIssueId]
  );

  const actions: Action[] = [
    {
      type: 'button',
      variant: 'ghostLink',
      leftIcon: 'action/backup',
      title: i18n.t('entity.document.actions.uploadDocument'),
      onClick: openUploadDialog,
      isDisabled: !canEditFastTrackIssue,
      tooltip: !canEditFastTrackIssue
        ? i18n.t('module.warehouse.label.missingFastTrackIssueEditPermission')
        : undefined,
      'data-testid': suffixTestId('action.addDocument', props),
    },
  ];

  return (
    <Section data-testid={suffixTestId('wrapper', props)}>
      <Card title={i18n.t('page.warehouse.labels.documents')} actions={actions}>
        <Box height="100%">
          <DmsDataGrid
            gridCode={DOCUMENTS_GRID_CODE}
            emptyState={{headline: i18n.t('general.labels.noData')}}
            actionCallback={rowActions}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            autoHeight
            data-testid={DOCUMENTS_GRID_CODE}
          />
        </Box>
      </Card>

      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={() => {
          refreshDmsDatagrid(DOCUMENTS_GRID_CODE);
          props.onDocumentAction();
        }}
        contextTarget={CONTEXT_TARGET}
        contextId={fastTrackIssueId}
        bulkContext={[{target: CONTEXT_TARGET, targetId: fastTrackIssueId}]}
        data-testid={suffixTestId('upload-document', props)}
      />
    </Section>
  );
}

const DOCUMENTS_GRID_CODE = 'document-context-list-warehouse-fast-track';
const CONTEXT_TARGET = 'warehouse-fast-track';
